/* Sessions — now backed by the real Cognito pool.
   ─────────────────────────────────────────────────────────────────────────────
   Identity comes from the ID token's claims — `sub`, `email` and
   `cognito:groups` — which authorise a request without a database read (D17).
   Ownership is the Cognito `sub` (D44).

   `name` and `team` do **not** come from the token (D55). They are profile
   rather than authorisation, they change while a token is live, and they are
   read from `GET /me` and cached under `mv.profile`.

   There is no session cookie and no backend-for-frontend: tokens live in
   localStorage and the ID token goes to the API in an Authorization header
   (D47). The mechanics are in `tokens.ts`.

   Both ways in are **managed login** — see `signInWithEmail` for why the email
   path cannot be done from our own screen. */

import { useEffect, useState } from 'react';
import { beginSignIn, completeSignIn, currentIdToken, decode, readTokens, writeTokens } from './tokens';
import { AUTH } from './auth-config';
import { ApiError, api } from './api';
import { TEAM_FALLBACK, type PatchMeRequest, type SessionInfo, type Team } from '@mv/contracts';


/**
 * Re-exported, not redefined. This file used to carry its own copy of the team
 * list beside the one in `@mv/contracts`, and a second copy is how the client
 * and the server come to disagree about who is on what team. `Team` is the
 * contract's; everything here imports it from this module as it always did.
 */
export { TEAMS, TEAM_FALLBACK, type Team } from '@mv/contracts';

export interface Session {
  userId: string;
  email: string;
  name: string;
  provider: 'google' | 'email' | 'admin';
  team: Team;
  isAdmin: boolean;
  /** Drives the one-time /welcome screen and nothing else. */
  needsWelcome: boolean;
  signedInAt: number;
}

const KEY = 'mv.session';
/**
 * The profile last read from `GET /me`, keyed by `sub`.
 *
 * Cached so that a boot renders a name and a flag before the network answers,
 * and so the app still shows them with no network at all — which is the whole
 * reason these were token claims to begin with. The difference is that a cache
 * is allowed to be replaced by the truth a moment later; a claim was not, for
 * an hour.
 *
 * Keyed by `sub` so a second guest signing in on a shared phone never renders
 * the first one's name while `/me` is in flight.
 */
const PROFILE_KEY = 'mv.profile';
/** Which `sub` has already seen /welcome. Local by design — a one-time
 *  nicety does not deserve a round trip or a token claim. */
const WELCOMED_KEY = 'mv.welcomed';

const listeners = new Set<() => void>();
let current: Session | null = read();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function commit(next: Session | null) {
  current = next;
  if (next) localStorage.setItem(KEY, JSON.stringify(next));
  else localStorage.removeItem(KEY);
  listeners.forEach((fn) => fn());
}

export function getSession(): Session | null { return current; }

export function useSession(): Session | null {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return current;
}

/**
 * Build a session from the ID token's claims. Nothing here is decided locally:
 * `sub`, team and display name all come from Cognito, which is what makes
 * ownership survive a new phone or a cleared browser (D44).
 */
interface CachedProfile { sub: string; name: string; team: Team }

function readProfile(sub: string): CachedProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    const p = raw ? (JSON.parse(raw) as CachedProfile) : null;
    return p && p.sub === sub ? p : null;
  } catch {
    return null;
  }
}

/**
 * Is the session that started an in-flight request still the one signed in?
 *
 * Every write that happens after an `await` has to ask. A sign-out or a
 * same-tab session swap can land in between, and a write that does not check
 * lands on somebody else — the cache restored after `signOut` cleared it, or
 * one guest's name committed onto another guest's id.
 */
function stillMine(userId: string): boolean {
  return current?.userId === userId;
}

function writeProfile(p: CachedProfile) {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch { /* private mode */ }
}

/**
 * Identity and authorisation from the token; name and team from the cache.
 *
 * **The token no longer carries a name or a team (D55).** It carries `sub`,
 * `email` and `cognito:groups` — the things that are fixed for the life of a
 * session and that authorise a request without a database read, which is what
 * D17 was always arguing. Profile is mutable and is read from `GET /me`.
 *
 * This function is synchronous and stays that way, so a boot paints
 * immediately. It fills profile from the last known values and `loadProfile`
 * replaces them when the network answers.
 */
function fromToken(idToken: string): Session | null {
  const c = decode(idToken);
  if (!c) return null;
  const groups = c['cognito:groups'] ?? [];
  const federated = (c.identities ?? []).some((i) => i.providerName === 'Google');
  const cached = readProfile(c.sub);
  const session: Session = {
    userId: c.sub,
    email: (c.email ?? '').toLowerCase(),
    name: cached?.name ?? 'Guest',
    provider: groups.includes('admin') ? 'admin' : federated ? 'google' : 'email',
    team: cached?.team ?? TEAM_FALLBACK,
    isAdmin: groups.includes('admin'),
    // Local, and deliberately so — /welcome is a one-time nicety, not identity,
    // and does not deserve a round trip or a row of its own.
    needsWelcome: localStorage.getItem(WELCOMED_KEY) !== c.sub,
    signedInAt: Date.now(),
  };
  commit(session);
  return session;
}

/**
 * Read the authoritative profile and fold it into the session.
 *
 * Called once from `restore()` at boot, and again by the screens that state a
 * profile outright — Profile and Welcome — because those are where a stale name
 * or team is actually noticed, and where an organiser will have just told
 * somebody to look.
 *
 * Silent on failure. Offline, or mid-deploy, the cached profile is still the
 * right thing to show; there is nothing useful to say to a guest about it and
 * nothing for them to do.
 */
export async function loadProfile(): Promise<Session | null> {
  if (!current) return null;
  try {
    const me = await api<SessionInfo>('/me');
    // The identity check comes first. A sign-out, or a session swap in the same
    // tab via the email-code path, can land while this request is in flight;
    // writing the cache before checking would leave one guest's profile stored
    // under the next one's session. `readProfile` re-checks `sub` on the way
    // out, so this was already safe — but by accident rather than on purpose.
    if (!stillMine(me.userId)) return current;
    writeProfile({ sub: me.userId, name: me.displayName, team: me.team });
    commit({ ...current!, name: me.displayName, team: me.team });
  } catch {
    // Keep what is cached.
  }
  return current;
}

/**
 * Google, in one tap — `identity_provider=Google` skips Cognito's own provider
 * chooser (D10). This never returns: the page navigates to managed login.
 */
export async function signInWithGoogle(): Promise<never> {
  if (!navigator.onLine) throw new Error('offline');
  return beginSignIn('Google');
}

/**
 * The email path, and it is a redirect rather than our own screen.
 *
 * A guest signing in for the first time does not yet exist in the pool, and
 * Cognito's EMAIL_OTP challenge signs in an *existing* user — the API has no
 * single call that creates a guest and sends them a code. Managed login does
 * both. Calling InitiateAuth ourselves looks like it works, because
 * `preventUserExistenceErrors` returns a challenge for an unknown address on
 * purpose, and then fails at the code with no way to tell a typo from a
 * stranger.
 *
 * That is exactly the silent lockout D46 exists to prevent, so the redirect is
 * the safe shape. See `EmailCode.tsx`, which this leaves unused.
 */
/** Kept for the fallback case: if `/auth/start` is ever unavailable, managed
 *  login can still get a guest in. Not on the normal path. */
export async function signInWithEmailHosted(): Promise<never> {
  if (!navigator.onLine) throw new Error('offline');
  return beginSignIn();
}

/** Called by /auth/callback with `?code=`. */
export async function completeAuthRedirect(code: string): Promise<Session> {
  const tokens = await completeSignIn(code);
  const session = fromToken(tokens.idToken);
  if (!session) throw new Error('bad_token');
  // **Awaited, unlike in `restore()`.** There, a cached profile is already on
  // screen and putting the first paint behind a round trip would make every
  // launch wait. Here there is no cache — this is a first sign-in, or a
  // returning guest on a new phone — and the very next screen is the team
  // reveal. Without this it renders "You're in, Guest" over the fallback flag,
  // which is the one screen on the site whose whole job is telling somebody
  // which team is theirs. The guest is already waiting on a sign-in round trip;
  // one more is not what they will notice.
  return (await loadProfile()) ?? session;
}

/** The header value for an API call, refreshing silently if needed. Null means
 *  the 30-day refresh token is gone too, and the guest must sign in again. */
export async function authHeader(): Promise<Record<string, string>> {
  const token = await currentIdToken();
  return token ? { Authorization: token } : {};
}

/**
 * Rename the guest, on the server and here.
 *
 * **This used to write to `localStorage` and nothing else**, which meant the
 * welcome screen's "your name, as it will show on your photos" did not: nothing
 * wrote `guests.displayName` after registration, `restore()` rebuilt the
 * session from the token on the next page load and overwrote it, and every
 * photograph was attributed to whatever the identity provider gave at first
 * sign-in — for the email path, the part of the address before the `@`. D55.
 *
 * Optimistic, then corrected. The input is the guest's own typing, so showing
 * it back to them immediately is right; if the write fails the server's value
 * is put back and the caller is told, because a name that looks saved and is
 * not is the failure this whole change exists to remove.
 */
export async function updateName(
  name: string,
): Promise<{ ok: boolean; name?: string; error?: string }> {
  if (!current) return { ok: false };
  const trimmed = name.trim();
  if (!trimmed || trimmed === current.name) return { ok: true, name: current.name };

  const before = current;
  commit({ ...current, name: trimmed });
  try {
    const res = await api<{ displayName: string }>('/me', {
      method: 'PATCH',
      json: { displayName: trimmed } satisfies PatchMeRequest,
    });
    // `current.team`, not `before.team`. The latter was captured before the
    // await, and Profile — the screen this is wired to — calls `loadProfile()`
    // on mount, so a team correction can land while the rename is in flight.
    // Writing the closed-over value would put the pre-correction team back in
    // the cache used for the next boot's first paint. The same `before`/
    // `current` mismatch across an await as the field bug above, one layer down.
    //
    // `stillMine` is the guard `loadProfile` applies for the same reason, and
    // omitting it here was an oversight rather than a judgement: a sign-out
    // landing mid-flight clears `mv.profile` and this would write it straight
    // back, and a same-tab session swap — a second email-code verification —
    // would commit the outgoing guest's new name onto the incoming guest's
    // session, leaving one person's name over another's id and team.
    if (!stillMine(before.userId)) return { ok: true, name: res.displayName };
    writeProfile({
      sub: before.userId,
      name: res.displayName,
      team: current?.team ?? before.team,
    });
    commit({ ...current!, name: res.displayName });
    // The stored name, not the typed one. `cleanDisplayName` trims and collapses
    // runs of whitespace server-side, so "  Sarah   Jones  " is stored — and
    // shown on every tile — as "Sarah Jones". A field still displaying the
    // original is the same quiet disagreement between client and server that
    // D55 exists to remove, on the one screen that is supposed to be the
    // authority on this guest's name.
    return { ok: true, name: res.displayName };
  } catch (err) {
    // Same guard on the way out. Putting the old name back is only correct for
    // the session that asked for the change.
    if (stillMine(before.userId)) commit({ ...current!, name: before.name });
    // The server's own sentence when it sent one. `cleanDisplayName` rejects a
    // name for reasons a guest can act on — too long, or nothing left after the
    // control characters come out — and telling them to check their wifi
    // instead is advice that cannot help. A 5xx is not the guest's to fix, so
    // that keeps the network wording.
    const useful = err instanceof ApiError && err.status >= 400 && err.status < 500;
    return {
      ok: false,
      error: useful
        ? (err as ApiError).message
        : 'That name did not save. Check your connection.',
    };
  }
}

export function completeWelcome() {
  if (!current) return;
  localStorage.setItem(WELCOMED_KEY, current.userId);
  commit({ ...current, needsWelcome: false });
}

/**
 * Restores a session on page load from the stored tokens, refreshing silently
 * if the hour is up. This is what "stays signed in for 30 days" actually is —
 * the refresh token, not the storage mechanism (D47).
 */
export async function restore(): Promise<Session | null> {
  if (!readTokens()) return null;
  const token = await currentIdToken();
  if (!token) { commit(null); return null; }
  const session = fromToken(token);
  // Not awaited. The app paints from the cached profile straight away and
  // re-renders if `/me` disagrees — putting the first paint behind a round trip
  // would make every launch wait on the resort's wifi.
  if (session) void loadProfile();
  return session;
}

export function signOut() {
  writeTokens(null);
  // The cached profile goes with the tokens. Leaving it would show the next
  // person to sign in on this phone the previous guest's name for as long as
  // `/me` took to answer.
  try { localStorage.removeItem(PROFILE_KEY); } catch { /* private mode */ }
  commit(null);
}

/**
 * NOT WIRED — deliberately left failing rather than left permissive.
 *
 * It previously accepted any non-empty password, which is the worst possible
 * placeholder for the one credential D40 calls the real thing worth
 * protecting. `admin-web` is SRP-only, and SRP is not something to hand-roll;
 * the options are a client library, enabling USER_PASSWORD_AUTH on that client,
 * or putting the admin through managed login too. That is a decision, not an
 * implementation detail, so it is not being made here.
 */
/**
 * The email path, on our own screen (D46, D10).
 *
 * Two calls, because Cognito has no single one. `/auth/start` makes the user
 * exist — a first-time guest does not, and EMAIL_OTP only signs in someone who
 * does. Then `InitiateAuth` sends the code exactly as the pool is configured
 * to, and `verifyCode` answers the challenge.
 *
 * Managed login could do both on its own page, and that is the version this
 * replaces: it could not be made to look like the rest of the site, and it
 * offered a second "Continue with Google" button next to the one the guest had
 * already walked past.
 */
export interface CodeRequest { email: string; sentAt: number }

export class CodeError extends Error {
  constructor(public kind: 'invalid' | 'expired' | 'offline' | 'rate_limited') { super(kind); }
}

/**
 * Cognito's two failure modes here mean different things to a guest, and
 * collapsing them is what makes a mistyped digit look like a dead end.
 * Measured against the live pool, 26 Aug 2026:
 *
 *   CodeMismatchException  — wrong digits, session still good. Retry works.
 *   NotAuthorizedException — the challenge is spent. Retrying is hopeless even
 *                            with the right code; only a new code helps.
 *
 * The session survives the first couple of wrong answers and then starts
 * returning NotAuthorized, with no clean cut-off — so this reads the error
 * rather than counting attempts.
 */
function codeErrorFrom(err: unknown): CodeError {
  const name = (err as { __type?: string; name?: string })?.__type
    ?? (err as { name?: string })?.name ?? '';
  return new CodeError(name.includes('CodeMismatch') ? 'invalid' : 'expired');
}

const PENDING_KEY = 'mv.pendingEmail';
const cognitoIdp = `https://cognito-idp.${AUTH.region}.amazonaws.com/`;

async function idp(target: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(cognitoIdp, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw json;
  return json;
}

export async function requestCode(email: string): Promise<CodeRequest> {
  if (!navigator.onLine) throw new CodeError('offline');
  const address = email.trim().toLowerCase();

  const started = await fetch(`${AUTH.api}/auth/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: address }),
  });
  if (started.status === 429) throw new CodeError('rate_limited');
  if (!started.ok) throw new CodeError('invalid');

  const res = await idp('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: AUTH.guestClientId,
    AuthParameters: { USERNAME: address, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  });

  const pending: CodeRequest = { email: address, sentAt: Date.now() };
  // The Cognito challenge session is what ties the code to this request. It is
  // single-use and short-lived, so sessionStorage rather than localStorage.
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, session: res.Session }));
  return pending;
}

export function getPendingCodeRequest(): CodeRequest | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as CodeRequest) : null;
  } catch {
    return null;
  }
}

export function clearPendingCodeRequest() { sessionStorage.removeItem(PENDING_KEY); }

export async function verifyCode(code: string): Promise<Session> {
  if (!navigator.onLine) throw new CodeError('offline');
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) throw new CodeError('expired');
  const pending = JSON.parse(raw) as CodeRequest & { session: string };

  let res: Record<string, unknown>;
  try {
    res = await idp('RespondToAuthChallenge', {
      ChallengeName: 'EMAIL_OTP',
      ClientId: AUTH.guestClientId,
      Session: pending.session,
      ChallengeResponses: { USERNAME: pending.email, EMAIL_OTP_CODE: code },
    });
  } catch (err) {
    const failure = codeErrorFrom(err);
    // A spent challenge cannot be retried, so drop it. That makes "Send another
    // code" the only remaining action, which is exactly the right one — rather
    // than leaving a guest retyping a correct code into a dead session.
    if (failure.kind === 'expired') clearPendingCodeRequest();
    throw failure;
  }

  const auth = res.AuthenticationResult as
    | { IdToken: string; AccessToken: string; RefreshToken: string; ExpiresIn: number }
    | undefined;
  if (!auth) throw new CodeError('invalid');

  writeTokens({
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken,
    expiresAt: Date.now() + auth.ExpiresIn * 1000,
  });
  clearPendingCodeRequest();
  const session = fromToken(auth.IdToken);
  if (!session) throw new CodeError('invalid');
  // See `completeAuthRedirect` — same reason, the other way in. `loadProfile`
  // swallows its own failures and answers with what it has, so a guest who
  // signs in and immediately loses the network still gets through; they land on
  // the reveal with the fallback rather than stuck on a code screen.
  return (await loadProfile()) ?? session;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
